import React, {useEffect, useState, useMemo} from 'react';

import { ReactComponent as PFC } from "../customIcons/PFC.svg";
import {useDarkMode} from "../contexts/darkModeContext";
import {
    DocumentChartBarIcon,
    NoSymbolIcon, PercentBadgeIcon, UserGroupIcon,
} from "@heroicons/react/24/outline";
import {Button} from "@headlessui/react";
import {collection, getDocs} from "firebase/firestore";
import {db} from "../firebase/firebase";
import {useAuth} from "../contexts/authContext";
import {getYearString} from "./GLOBAL/GlobalFunctions";
import Loading from "./Widgets/Loading";
import PerformanceCard from "./Performance/PerformanceCard";

import { ReactComponent as CSRICON } from "../customIcons/CSR.svg";
import ActionPillButton from "./Widgets/ActionPillButton";
import WeeklyBreakdown from "./Performance/WeeklyBreakdown";

export function calculateTimeDifference(timestamp1, timestamp2, returnMinutesOnly = false) {
  if (!timestamp1 || !timestamp2) return returnMinutesOnly ? 0 : "0 minutes";

  const WORK_START = 8;
  const WORK_END = 17;
  const WORK_DAY_MINUTES = (WORK_END - WORK_START) * 60;

  let start = new Date(timestamp1);
  let end = new Date(timestamp2);

  if (start > end) [start, end] = [end, start];

  function isWeekend(date) {
    const d = date.getDay();
    return d === 0 || d === 6;
  }

  function nextWorkStart(date) {
    const d = new Date(date);

    while (true) {
      const day = d.getDay();

      if (day === 0) { // Sunday
        d.setDate(d.getDate() + 1);
        d.setHours(WORK_START,0,0,0);
        continue;
      }

      if (day === 6) { // Saturday
        d.setDate(d.getDate() + 2);
        d.setHours(WORK_START,0,0,0);
        continue;
      }

      if (d.getHours() >= WORK_END) {
        d.setDate(d.getDate() + 1);
        d.setHours(WORK_START,0,0,0);
        continue;
      }

      if (d.getHours() < WORK_START) {
        d.setHours(WORK_START,0,0,0);
      }

      break;
    }

    return d;
  }

  function previousWorkEnd(date) {
    const d = new Date(date);

    while (true) {
      const day = d.getDay();

      if (day === 0) { // Sunday
        d.setDate(d.getDate() - 2);
        d.setHours(WORK_END,0,0,0);
        continue;
      }

      if (day === 6) { // Saturday
        d.setDate(d.getDate() - 1);
        d.setHours(WORK_END,0,0,0);
        continue;
      }

      if (d.getHours() >= WORK_END) {
        d.setHours(WORK_END,0,0,0);
      }

      if (d.getHours() < WORK_START) {
        d.setDate(d.getDate() - 1);
        d.setHours(WORK_END,0,0,0);
        continue;
      }

      break;
    }

    return d;
  }

  start = nextWorkStart(start);
  end = previousWorkEnd(end);

  if (end <= start) return returnMinutesOnly ? 0 : "0 minutes";

  let totalMinutes = 0;

  function sameDay(a, b) {
    return a.toDateString() === b.toDateString();
  }

  if (sameDay(start, end)) {
    totalMinutes = Math.floor((end - start) / 60000);
  } else {

    const endOfStartDay = new Date(start);
    endOfStartDay.setHours(WORK_END,0,0,0);

    totalMinutes += Math.floor((endOfStartDay - start) / 60000);

    const startOfEndDay = new Date(end);
    startOfEndDay.setHours(WORK_START,0,0,0);

    totalMinutes += Math.floor((end - startOfEndDay) / 60000);

    const nextDay = new Date(start);
    nextDay.setDate(nextDay.getDate() + 1);
    nextDay.setHours(0,0,0,0);

    const prevDay = new Date(end);
    prevDay.setDate(prevDay.getDate() - 1);
    prevDay.setHours(0,0,0,0);

    if (nextDay <= prevDay) {
      const totalDays = Math.floor((prevDay - nextDay) / 86400000) + 1;

      for (let i = 0; i < totalDays; i++) {
        const d = new Date(nextDay);
        d.setDate(d.getDate() + i);

        if (!isWeekend(d)) {
          totalMinutes += WORK_DAY_MINUTES;
        }
      }
    }
  }

  if (returnMinutesOnly) return totalMinutes;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let result = "";

  if (hours > 0) result += `${hours} hour${hours !== 1 ? "s" : ""}`;
  if (hours > 0 && minutes > 0) result += ", ";
  if (minutes > 0 || hours === 0) result += `${minutes} minute${minutes !== 1 ? "s" : ""}`;

  return result;
}

const Performance = () => {

    const { darkMode } = useDarkMode();
    
    const [showReport, setShowReport] = useState(false);
    
    const [alertsOn] = useState(() => {
        const storedSettings = localStorage.getItem('alertsOn');
        return storedSettings ? JSON.parse(storedSettings).alertsOn : false; // Default to false if null
    });
    
    const { currentUser } = useAuth();
    
    const [pastOrders, setPastOrders] = useState([]);
    const [selectedRep, setSelectedRep] = useState('');
    // eslint-disable-next-line
    const [showCurrentWeekOnly, setShowCurrentWeekOnly] = useState(false);
    
    function filterCurrentWeekOrders(orders) {
      if (!orders || orders.length === 0) return [];
    
      const now = new Date();
    
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
    
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 7);
    
      return orders.filter(order => {
        if (!order.csrTime) return false;
    
        const orderDate = new Date(order.csrTime);
    
        return orderDate >= startOfWeek && orderDate < endOfWeek;
      });
    }
    
    // eslint-disable-next-line
    const [updating, setUpdating] = useState(false);

    const onUpdate = async () => {
        setUpdating(true);
        const startTime = Date.now();

        try {
            const performanceRef = collection(db, `Users/${currentUser.uid}/Performance`);
            const querySnapshot = await getDocs(performanceRef);

            if (!querySnapshot.empty) {
                const performanceData = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Update state with fetched notifications
                setPastOrders(performanceData);

                // Save notifications to localStorage
                localStorage.setItem(`performance_${currentUser.uid}`, JSON.stringify(performanceData));
            } else {
                console.log('No performance data found.');
            }
        } catch (error) {
            console.error("Error fetching performance: ", error);
        }

        const endTime = Date.now();
        const timeElapsed = (endTime - startTime) / 1000;
        setUpdating(false);
        if (alertsOn) {
            alert(`Data Updated in ${timeElapsed} secs`);
        }
    };
    

    async function fetchPerformanceData(CSR) {
        setUpdating(true);
        const startTime = Date.now();
        
        try {
            setSelectedRep(CSR);
            
            const performanceRef = collection(db, `Users/${CSR}/Performance`);
            const querySnapshot = await getDocs(performanceRef);

            if (!querySnapshot.empty) {
                const performanceData = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Update state with fetched notifications
                setPastOrders(performanceData);

            } else {
                console.log('No performance data found.');
            }
            
        } catch (error) {
            console.error('Error fetching performance:', error);
        }
    
        const endTime = Date.now();
        const timeElapsed = (endTime - startTime) / 1000;
        setUpdating(false);
    
        if (alertsOn) {
            alert(`Orders Updated in ${timeElapsed} secs`);
        }
    }

    
    const handleButtonClick = (CSR) => {
        fetchPerformanceData(CSR);
    };
 
    function classNames(...classes) {
      return classes.filter(Boolean).join(' ')
    }
    
    function calculateAverageTimeDifference(dataArray, raw = false) {
      if (!dataArray || dataArray.length === 0) return raw ? 0 : "Select Rep...";
    
      let totalMinutes = 0;
      let validEntriesCount = 0;
    
      for (const item of dataArray) {
        const { submittedTime, csrTime } = item;
    
        if (typeof submittedTime === "number" && typeof csrTime === "number") {
          const officeMinutes = calculateTimeDifference(submittedTime, csrTime, true);
          totalMinutes += officeMinutes;
          validEntriesCount++;
        }
      }
    
      if (validEntriesCount === 0) return raw ? 0 : "No valid time pairs found";
    
      const avgMinutes = totalMinutes / validEntriesCount;
    
      // Return raw minutes if requested
      if (raw) return Math.round(avgMinutes);
    
      const hours = Math.floor(avgMinutes / 60);
      const minutes = Math.round(avgMinutes % 60);
    
      let result = "";
      if (hours > 0) result += `${hours} h`;
      if (hours > 0 && minutes > 0) result += ", ";
      if (minutes > 0 || hours === 0) result += `${minutes} min${minutes !== 1 ? "s" : ""}`;
    
      return result;
    }
    
    function findFastestTimeDifference(dataArray) {
      if (!dataArray || dataArray.length === 0) return "Select Rep...";
    
      let fastestMinutes = Infinity;
    
      for (const item of dataArray) {
        const { submittedTime, csrTime } = item;
    
        if (typeof submittedTime === "number" && typeof csrTime === "number") {
          const officeMinutes = calculateTimeDifference(submittedTime, csrTime, true);
          if (officeMinutes < fastestMinutes) {
            fastestMinutes = officeMinutes;
          }
        }
      }
    
      if (fastestMinutes === Infinity) return "No valid time pairs found";
    
      const hours = Math.floor(fastestMinutes / 60);
      const minutes = Math.round(fastestMinutes % 60);
    
      let result = "";
      if (hours > 0) result += `${hours} h`;
      if (hours > 0 && minutes > 0) result += ", ";
      if (minutes > 0 || hours === 0) result += `${minutes} min${minutes !== 1 ? "s" : ""}`;

    
      return result;
    }
    
    function findSlowestTimeDifference(dataArray) {
      if (!dataArray || dataArray.length === 0) return "Select Rep...";
    
      let slowestMinutes = 0;
    
      for (const item of dataArray) {
        const { submittedTime, csrTime } = item;
    
        if (typeof submittedTime === "number" && typeof csrTime === "number") {
          const officeMinutes = calculateTimeDifference(submittedTime, csrTime, true);
          if (officeMinutes > slowestMinutes) {
            slowestMinutes = officeMinutes;
          }
        }
      }
    
      if (slowestMinutes === 0) return "No valid time pairs found";
    
      const hours = Math.floor(slowestMinutes / 60);
      const minutes = Math.round(slowestMinutes % 60);
    
      let result = "";
      if (hours > 0) result += `${hours} h`;
      if (hours > 0 && minutes > 0) result += ", ";
      if (minutes > 0 || hours === 0) result += `${minutes} min${minutes !== 1 ? "s" : ""}`;

    
      return result;
    }
    
    
    
    const [CSRs, setCSRs] = useState([]);
    
    useEffect(() => {
        const fetchCSR = async () => {
            const usersCollection = collection(db, 'Users');
            const userSnapshot = await getDocs(usersCollection);
            
            const filteredUserList = userSnapshot.docs
                .map(doc => ({
                    id: doc.id,
                    name: `${doc.data().fname} ${doc.data().lname}`,
                    accountLevel: doc.data().accountLevel,
                    email: doc.data().email,
                    phone: doc.data().phone,
                }))
                .filter(user => ['CSR', 'A'].includes(user.accountLevel)); // ✅ updated
    
            setCSRs(filteredUserList);
        };
        
        fetchCSR();
    }, []);
    
    
    
    
    const [status, setStatus] = useState('C');
    
    const [secondaryNavigation, setSecondaryNavigation] = useState([
      // { name: 'Dashboard', href: '#', current: false },
      // { name: 'Archived Performance', href: 'A', current: false },
      { name: 'Current Performance', href: 'C', current: true },
    ]);
    
    const LabelMapping = {
        A: "Archived",
        C: "Current",
    };
    
    const averageTime = useMemo(() => calculateAverageTimeDifference(pastOrders), [pastOrders]);
    const fastestTime = useMemo(() => findFastestTimeDifference(pastOrders), [pastOrders]);
    const slowestTime = useMemo(() => findSlowestTimeDifference(pastOrders), [pastOrders]);

    const stats = [
      { name: `Number of Orders Completed`, value: `${pastOrders.length}`, unit: `orders` },
      { name: 'Average Response Time', stat: "A", value: `${averageTime}` },
      { name: `Fastest Response Time`, value: `${fastestTime}` },
      { name: 'Slowest Response Time', stat: "P", value: `${slowestTime}` },
    ]
    
    const getStatusColor = (status) => {
        switch (status) {
            case 'C':
                return 'bg-green-600/25 text-green-600'; // Active
            case 'A':
                return 'bg-yellow-500/25 text-yellow-500'; // Pending
            default:
                return 'bg-gray-400/25 text-gray-400'; // Fallback
        }
    };
    
    const [collapse] = useState(false);
    
    
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);

    const weeklyOrders = useMemo(() => {
      return filterCurrentWeekOrders(pastOrders);
    }, [pastOrders]);

    const displayOrders = showCurrentWeekOnly ? weeklyOrders : pastOrders;
    
    const totalPages = Math.ceil(displayOrders.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentOrders = displayOrders.slice(startIndex, startIndex + itemsPerPage);
    
    const avgRaw = useMemo(() =>
      calculateAverageTimeDifference(pastOrders, true),
    [pastOrders]);
    
    const sortedOrders = useMemo(() =>
      [...currentOrders].sort((a, b) => b.csrTime - a.csrTime),
    [currentOrders]);

    function getCompletionContext(timestamp) {
      const d = new Date(timestamp);
      const day = d.getDay();
      const hour = d.getHours();

      const weekend = day === 0 || day === 6;
      const beforeOpen = !weekend && hour < 8;
      const afterClose = !weekend && hour >= 17;
      const outsideOfficeHours = weekend || beforeOpen || afterClose;

      return { weekend, beforeOpen, afterClose, outsideOfficeHours };
    }

    const completionStats = useMemo(() => {
      let weekend = 0;
      let beforeOpen = 0;
      let afterClose = 0;
      let outsideOfficeHours = 0;

      for (const order of displayOrders) {
        if (!order.csrTime) continue;

        const ctx = getCompletionContext(order.csrTime);

        if (ctx.weekend) weekend++;
        if (ctx.beforeOpen) beforeOpen++;
        if (ctx.afterClose) afterClose++;
        if (ctx.outsideOfficeHours) outsideOfficeHours++;
      }

      return [
        { name: 'Outside Office Hours', value: outsideOfficeHours },
        { name: 'Weekend Completions', value: weekend },
        { name: 'Before Open', value: beforeOpen },
        { name: 'After Close', value: afterClose }
      ];

    }, [displayOrders]);
    
    
    return (
        <div className={`${darkMode ? 'text-white' : 'text-black'} flex h-[calc(100dvh-3.5rem)] w-full transition-all duration-500 ease-in-out`}>
            
            {updating && (
                <Loading/>
            )}
            
            {/*Redesign*/}
            <div className={`animate-fadeIn z-20 ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-black'} w-full`}>
                
                <main>
                    
                    {/*Header*/}
                    <header>
                        
                       
                        {/* Heading */}
                        <div className={`${collapse ? 'hidden' : ''} flex flex-col items-start justify-between gap-x-8 ${darkMode ? ' bg-gray-700/10' : 'bg-neutral-300/50'} px-4 py-2 sm:flex-row sm:items-center`}>
                            <div>
                                <div className="flex items-center gap-x-3">
                                    <div className={`flex-none rounded-full ${getStatusColor(status)} p-1`}>
                                        <div className="size-2 rounded-full bg-current"/>
                                    </div>
                                    <h1 className="flex gap-x-3 text-base/7">
                                        <span className="font-semibold">CSR Performance</span>
                                        <span className="font-semibold text-gray-500">{getYearString()}</span>
                                        <span className="text-gray-600">/</span>
                                        <span className="font-semibold">{LabelMapping[status]}</span>
                                        
                                        <ActionPillButton
                                            onClick={() => setShowReport(!showReport)}
                                            color={`green`}
                                            icon={DocumentChartBarIcon}
                                            label={`Weekly Report`}
                                        />
                                        
                                    </h1>
                                </div>
                                <p className="mt-2 text-xs/6 text-gray-500">This is the performance of the CSR reps in the time that they complete an order.</p>
                            </div>
                            
                            <div className={`space-x-3 flex items-center`}>
                                
                                <Button onClick={onUpdate} className="hidden flex items-center hover:scale-105 order-first flex-none rounded-full bg-green-600/10 px-2 py-1 text-xs font-medium text-green-600 ring-1 ring-green-600/30 ring-inset sm:order-none">
                                    <PercentBadgeIcon className={`size-4 text-green-600 mr-2`}/>
                                    View Current Performance
                                </Button>
                                
                                <Button
                                  onClick={() => {
                                    setShowCurrentWeekOnly(prev => !prev);
                                    setCurrentPage(1);
                                  }}
                                  className={`flex items-center hover:scale-105 rounded-full px-3 py-1 h-fit text-xs font-medium ring-1 ring-inset
                                  ${showCurrentWeekOnly
                                    ? 'bg-blue-600 text-white ring-blue-600/30'
                                    : 'bg-green-600 text-white ring-green-600/30'
                                  }`}
                                >
                                  {showCurrentWeekOnly ? 'Showing: This Week' : 'Showing: All Orders'}
                                </Button>
                                
                                {/*Weekly Stats*/}
                                <div className={`divide-x flex border rounded-md overflow-hidden ${darkMode ? 'bg-darkMainColor border-neutral-700 divide-neutral-700 shadow-2xl' : 'bg-white shadow-sm'}`}>
                                    <div className={`flex flex-col divide-y ${darkMode ? 'divide-neutral-700' : ''}`}>
                                        <div className={`bg-gray-700/20 px-3 text-center py-0.5 text-xs text-green-600 `}>#</div>
                                        <div className={`text-xs px-3 py-0.5 text-center`}>{weeklyOrders.length || 'Select Rep...'}</div>
                                    </div>
                                    
                                    <div className={`flex flex-col divide-y ${darkMode ? 'divide-neutral-700' : ''}`}>
                                        <div className={`bg-gray-700/20 px-3 text-center py-0.5 text-xs text-green-600 `}>Weekly Average</div>
                                        <div className={`text-xs px-3 py-0.5 text-center`}>{calculateAverageTimeDifference(weeklyOrders)}</div>
                                    </div>
                                    
                                    <div className={`flex flex-col divide-y ${darkMode ? 'divide-neutral-700' : ''}`}>
                                        <div className={`bg-gray-700/20 px-3 text-center py-0.5 text-xs text-green-600 `}>Weekly Fastest</div>
                                        <div className={`text-xs px-3 py-0.5 text-center`}>{findFastestTimeDifference(weeklyOrders)}</div>
                                    </div>
                                    
                                    <div className={`flex flex-col divide-y ${darkMode ? 'divide-neutral-700' : ''}`}>
                                        <div className={`bg-gray-700/20 px-3 text-center py-0.5 text-xs text-green-600 `}>Weekly Slowest</div>
                                        <div className={`text-xs px-3 py-0.5 text-center`}>{findSlowestTimeDifference(weeklyOrders)}</div>
                                    </div>
                                
                                </div>
                                
                            </div>
                            
                        </div>
                        
                        {/* Stats */}
                        <div className={`${collapse ? 'hidden' : ''} grid grid-cols-1 ${darkMode ? ' bg-gray-700/10' : 'bg-neutral-300/50'} sm:grid-cols-2 lg:grid-cols-4`}>
                            
                            {stats.map((stat, statIdx) => {
                                
                                return (
                                    <div
                                        key={stat.name}
                                        className={classNames(
                                            statIdx % 2 === 1 ? 'sm:border-l' : statIdx === 2 ? 'lg:border-l' : '',
                                            `border-t ${darkMode ? 'border-neutral-700' : 'border-gray-300'} px-4 py-1`
                                        )}
                                    >
                                        <div className={`px-2 py-1 rounded-lg`}>
                                            <p className="text-sm/6 font-bold text-gray-500">{stat.name}</p>
                                            <p className="flex items-baseline gap-x-2">
                                                <span className="text-2xl font-semibold tracking-tight">{stat.value}</span>
                                                {stat.unit ? (
                                                    <span
                                                        className={`text-sm px-1 rounded-lg font-bold`}
                                                    >
                                                        {stat.unit}
                                                    </span>
                                                ) : null}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        
                        
                        </div>
                    
                    </header>
                    
                    {/* Activity list */}
                    <div className="">
                        
                        {/*Orders MAP*/}
                        <div className={`overflow-y-auto grid grid-cols-8 divide-x h-[calc(100dvh-13.8rem)] border-t scrollbar-hide ${darkMode ? 'text-white bg-darkMainColor border-neutral-700 divide-neutral-700' : 'text-black bg-white'} rounded-b-lg scrollbar-hide`}>
                            
                            {/*CSR*/}
                            <div className={`col-span-1 h-[calc(100dvh-13.9rem)] overflow-y-auto scrollbar-hide`}>
                                
                                {/*Header*/}
                                <div className={`py-2 flex items-center justify-between bg-gray-700/10 border-b ${darkMode ? 'border-neutral-700' : ''} text-sm px-2`}>
                                    <span className={``}>CSR List</span>
                                    <span className={`text-gray-500`}>{CSRs.length}</span>
                                </div>
                                
                                {/*CSR List*/}
                                <div className={`w-full border-b p-2 ${darkMode ? 'border-neutral-700' : ''}`}>
                                    
                                    {CSRs.length === 0 ? (
                                        <div className="w-full h-full flex justify-center items-center">
                                            <div className={`flex items-center border-2 border-dashed ${darkMode ? "text-white border-borderColor" : "text-black bg-neutral-50"} w-fit px-24 py-6 h-fit mr-auto ml-auto justify-center rounded-lg hover:scale-105`}>
                                                <div className="text-center flex items-center flex-col">
                                                    <UserGroupIcon className={`size-12 stroke-1 text-white`}/>
                                                    <h3 className="mt-2 text-sm font-semibold">No CSR Reps</h3>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className={`grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1  auto-rows-auto text-xs`}>
                                                {CSRs.map((CSR, index) => (
                                                    <Button
                                                        onClick={() => handleButtonClick(CSR.id)}
                                                        key={CSR.id} className={`flex items-center gap-x-2 ${selectedRep === CSR.id ? 'mx-2 bg-green-600/15 text-start rounded-r-lg border-l-4 border-green-600' : 'hover:bg-green-600/15 text-start hover:rounded-r-lg hover:border-l-4 hover:border-green-600'} p-2`}
                                                    >
                                                        <CSRICON className={`size-4`}/>
                                                        
                                                        {CSR.name}
                                                        
                                                    </Button>
                                                ))}
                                            
                                            </div>
                                        </>
                                    )}
                                
                                </div>
                                
                                {/*More Stats*/}
                                <div className={`w-full`}>

                                    <div className={`py-2 flex items-center justify-between bg-gray-700/10 border-b ${darkMode ? 'border-neutral-700' : ''} text-sm px-2`}>
                                        <span>Completion Stats</span>
                                        <span className={`text-gray-500`}>{completionStats.length}</span>
                                    </div>

                                    <div className={`flex flex-col text-xs`}>
                                        {completionStats.map((stat) => (
                                            <div
                                                key={stat.name}
                                                className={`flex items-center justify-between px-2 py-3 border-b ${darkMode ? 'border-neutral-700' : 'border-gray-200'}`}
                                            >
                                                <span className="text-gray-500">{stat.name}</span>
                                                <span className="font-semibold">{stat.value}</span>
                                            </div>
                                        ))}
                                    </div>

                                </div>
                                
                            </div>
                            
                            {/*Orders*/}
                            <div className={`col-span-7 h-[calc(100dvh-13.9rem)] overflow-y-auto scrollbar-hide`}>
                                
                                
                                
                                {displayOrders.length === 0 ? (
                                    // Display custom dialog if sprays array is empty
                                    <div className="flex w-full h-full justify-center items-center ">
                                        
                                        <div className={`flex flex-col  items-center w-fit px-8 py-2 h-fit mr-auto ml-auto justify-center rounded-lg hover:scale-105`}>
                                            
                                            <div className={`bg-gray-700/15 size-32 flex items-center justify-center rounded-full`}>
                                                <CSRICON className={`size-20 text-gray-500`}/>
                                            </div>
                                            <div className={`mt-2`}>No Stats</div>
                                            <p className=" text-gray-500 text-md line-clamp-1 flex">Select a CSR to view their orders</p>
                                        
                                        </div>
                                    
                                    </div>
                                ) : (
                                    // Display PFC Update components if sprays array is not empty
                                    <div className={`grid grid-cols-4 ${darkMode ? 'divide-white/10' : 'divide-black/10'}`}>
                                        {sortedOrders.map((order, index) => (
                                          <PerformanceCard
                                              key={order.id}
                                              order={order}
                                              index={index}
                                              avg={avgRaw}
                                          />
                                        ))}
                                        
                                        <nav
                                            aria-label="Pagination"
                                            className={`col-span-full flex items-center justify-between border-t border-gray-200 ${darkMode ? 'border-neutral-700' : ''} px-4 py-1 sm:px-6`}
                                        >
                                            <div className="flex items-center space-x-3">
                                                <p className="text-sm">
                                                    Showing <span
                                                    className="font-medium">{startIndex + 1}</span> to <span
                                                    className="font-medium">{Math.min(startIndex + itemsPerPage, displayOrders.length)}</span> of{' '}
                                                    <span className="font-medium">{displayOrders.length}</span> results
                                                </p>
                                                <select
                                                    value={itemsPerPage}
                                                    onChange={(e) => {
                                                        setItemsPerPage(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                    className={`border rounded-lg p-1 ${darkMode ? "bg-darkMainColor border-neutral-700" : ""}`}
                                                >
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={75}>75</option>
                                                    <option value={100}>100</option>
                                                </select>
                                            </div>
                                            <div className="flex flex-1 justify-between sm:justify-end">
                                                <Button
                                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                    className={`px-3 py-1 h-fit rounded-md ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "bg-green-600 text-white"}`}
                                                >
                                                    Previous
                                                </Button>
                                                <Button
                                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages}
                                                    className={`px-3 py-1 h-fit ml-3 rounded-md ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "bg-green-600 text-white"}`}
                                                >
                                                    Next
                                                </Button>
                                            </div>
                                        </nav>
                                    </div>
                                )}
                            
                            </div>
                        
                        </div>
                    
                    </div>
                
                </main>
            
            </div>
        
            
            <WeeklyBreakdown
                CSR={selectedRep}
                orders={pastOrders}
                onClose={() => setShowReport(false)}
                open={showReport}
            />
            
        
        </div>
    )
};


export default Performance;
